import { useCallback, useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import {
  Check,
  Crown,
  Zap,
  ArrowRight,
  Sparkles,
  ChevronDown,
  Shield,
  Server,
  CreditCard,
  Globe,
  Info,
} from "lucide-react";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { useTranslation } from "react-i18next";
import { extractAnnualPricingConfig } from "@/utils/pricingUtils";
import { isMaintenanceMode } from "@/utils/maintenanceMode";

const API_BASE_URL = String(process.env.REACT_APP_API_URL || "").trim().replace(/\/+$/, "");
const API = API_BASE_URL ? `${API_BASE_URL}/api` : "/api";
const APP_BASE_URL = String(process.env.REACT_APP_APP_URL || "").trim().replace(/\/+$/, "");

const ICONS = { free: null, starter: Sparkles, pro: Zap, business: Crown };
const COLORS = {
  free: "border-border",
  starter: "border-emerald-500/40",
  pro: "border-brand-primary",
  business: "border-purple-500/45 shadow-[0_0_24px_rgba(147,51,234,0.18)]",
};
const BADGE_COLORS = {
  free: "text-muted-foreground",
  starter: "text-emerald-500",
  pro: "text-brand-primary",
  business: "text-purple-400",
};
const BTN_CLASS = {
  starter: "bg-emerald-600 hover:bg-emerald-600/90 text-white",
  pro: "bg-brand-primary hover:bg-brand-primary/90 text-white",
  business:
    "bg-gradient-to-r from-purple-600 to-fuchsia-600 hover:from-purple-500 hover:to-fuchsia-500 text-white",
  downgrade: "bg-zinc-600 hover:bg-zinc-600/90 text-white",
};

const PLAN_NAME_KEYS = {
  free: "pricing.plan.free",
  starter: "pricing.plan.starter",
  pro: "pricing.plan.pro",
  business: "pricing.plan.business",
};
const REPORT_LANGUAGE_ORDER = ["id", "en"];
const DEFAULT_PRICING_VISIBILITY = {
  section_quota: true,
  section_detect: true,
  section_report: true,
  section_storage: true,
  section_support: true,
  row_monthly_limit: true,
  row_daily_limit: true,
  row_file_limit: true,
  row_cooldown: true,
  row_max_video_duration: true,
  row_max_audio_duration: true,
  row_detect_image: true,
  row_detect_video: true,
  row_detect_audio: true,
  row_report_score: true,
  row_report_ai_explain: true,
  row_report_languages: true,
  row_report_technical: true,
  row_report_pdf: true,
  row_storage_history: true,
  row_storage_upload: true,
  row_storage_grace_days: true,
  row_storage_credits: true,
  row_support_center: true,
  row_support_email: true,
  row_support_api: true,
};

const container = {
  hidden: { opacity: 0 },
  show: { opacity: 1, transition: { staggerChildren: 0.07 } },
};
const fadeUp = {
  hidden: { opacity: 0, y: 16 },
  show: { opacity: 1, y: 0, transition: { duration: 0.35 } },
};

export default function Pricing() {
  const { t, i18n } = useTranslation();
  const [plans, setPlans] = useState([]);
  const [creditPacks, setCreditPacks] = useState([]);
  const [openFaq, setOpenFaq] = useState(null);
  const [showComparison, setShowComparison] = useState(false);
  const [shouldScrollComparison, setShouldScrollComparison] = useState(false);
  const [billingCycle, setBillingCycle] = useState("monthly");
  const [annualBillingEnabled, setAnnualBillingEnabled] = useState(false);
  const [pricingVisibility, setPricingVisibility] = useState(DEFAULT_PRICING_VISIBILITY);
  const [plansLoading, setPlansLoading] = useState(true);
  const [plansError, setPlansError] = useState(null);
  const comparisonRef = useRef(null);
  const locale = i18n.language?.startsWith("id") ? "id-ID" : "en-US";
  const maintenanceMode = isMaintenanceMode();

  const formatNumber = (value) => Number(value || 0).toLocaleString(locale);
  const formatFileLimit = (mb) => {
    if (!mb) return "50 MB";
    if (mb >= 1024 && mb % 1024 === 0) return `${mb / 1024} GB`;
    return `${mb} MB`;
  };
  const formatDurationLimit = (seconds) => {
    const value = Number(seconds || 0);
    if (value <= 0) return t("pricing.compare.value.unlimited");
    const minutes = Math.round((value / 60) * 10) / 10;
    const minutesText = Number.isInteger(minutes)
      ? Number(minutes).toLocaleString(locale)
      : minutes.toLocaleString(locale, { minimumFractionDigits: 1, maximumFractionDigits: 1 });
    return t("pricing.compare.value.minutes", { value: minutesText });
  };
  const normalizePricingVisibility = (rawValue) => {
    const source = rawValue && typeof rawValue === "object" ? rawValue : {};
    return Object.keys(DEFAULT_PRICING_VISIBILITY).reduce((acc, key) => {
      acc[key] = source[key] !== undefined ? source[key] !== false : DEFAULT_PRICING_VISIBILITY[key];
      return acc;
    }, {});
  };
  const isVisible = (key) => pricingVisibility?.[key] !== false;
  const isMediaAllowed = (plan, mediaType) => {
    const raw = plan?.media_access?.[mediaType];
    if (raw === undefined || raw === null) return true;
    return !!raw;
  };
  const getPlanColorClass = (planId) => COLORS[planId] || "border-brand-primary/40";
  const getBadgeColorClass = (planId) => BADGE_COLORS[planId] || "text-brand-primary";
  const getButtonClass = (planId, isDowngrade) => {
    if (isDowngrade) return BTN_CLASS.downgrade;
    return BTN_CLASS[planId] || "bg-brand-primary hover:bg-brand-primary/90 text-white";
  };
  const getPlanLabel = (plan) => {
    const customName = String(plan?.name || "").trim();
    if (customName) return customName;
    const key = PLAN_NAME_KEYS[plan.id];
    return key ? t(key) : (plan?.id || "");
  };
  const buildQuotaItems = (plan, includeCooldownForFree = false) => {
    const items = [];
    if (isVisible("row_monthly_limit")) {
      items.push({
        text: t("pricing.feat.quota.monthly", {
          count: plan.limit,
        }),
        ok: true,
      });
    }
    if (isVisible("row_daily_limit")) {
      items.push({
        text: t("pricing.feat.quota.daily", {
          count: plan.daily_limit,
        }),
        ok: true,
      });
    }
    if (isVisible("row_file_limit")) {
      items.push({
        text: t("pricing.feat.quota.file", {
          size: formatFileLimit(plan.file_limit_mb),
        }),
        ok: true,
      });
    }
    if (isVisible("row_cooldown") && (plan.id !== "free" || includeCooldownForFree)) {
      items.push({
        text: t("pricing.feat.quota.cooldown", {
          seconds: plan.cooldown_s ?? 0,
        }),
        ok: true,
      });
    }
    if (isVisible("row_max_video_duration")) {
      items.push({
        text: t("pricing.feat.quota.maxVideoDuration", {
          value: formatDurationLimit(plan.max_video_duration_s),
        }),
        ok: true,
      });
    }
    if (isVisible("row_max_audio_duration")) {
      items.push({
        text: t("pricing.feat.quota.maxAudioDuration", {
          value: formatDurationLimit(plan.max_audio_duration_s),
        }),
        ok: true,
      });
    }
    return items;
  };
  const getPlanFeatures = (plan) => plan?.feature_overrides || {};
  const getReportLanguages = (plan) => {
    const featureMap = getPlanFeatures(plan);
    const raw = featureMap?.report_languages;
    const normalized = Array.isArray(raw)
      ? raw.map((lang) => String(lang || "").trim().toLowerCase()).filter((lang) => REPORT_LANGUAGE_ORDER.includes(lang))
      : [];
    const unique = Array.from(new Set(normalized));
    if (!unique.length) {
      return featureMap?.report_bilingual === false ? ["id"] : ["id", "en"];
    }
    if (!unique.includes("id")) unique.unshift("id");
    return REPORT_LANGUAGE_ORDER.filter((lang) => unique.includes(lang));
  };
  const reportLanguagesLabel = (plan) => getReportLanguages(plan)
    .map((lang) => t(`pricing.lang.${lang}`))
    .join(", ");
  const aiExplanationLabel = (plan) => {
    const level = String(getPlanFeatures(plan).ai_explanation_level || "basic").toLowerCase();
    if (level === "none") return t("pricing.compare.value.notAvailable");
    if (level === "full") return t("pricing.compare.value.full");
    return t("pricing.compare.value.basic");
  };
  const emailSupportLabel = (plan) => {
    const hours = Number(getPlanFeatures(plan).support_email_sla_hours || 0);
    if (hours <= 0) return t("pricing.compare.value.notAvailable");
    return t("pricing.feat.support.emailHours", { hours });
  };
  const detectionSummaryLabel = (plan) => {
    const labels = [];
    if (isMediaAllowed(plan, "image")) labels.push(t("pricing.feat.detect.image"));
    if (isMediaAllowed(plan, "video")) labels.push(t("pricing.feat.detect.video"));
    if (isMediaAllowed(plan, "audio")) labels.push(t("pricing.feat.detect.audio"));
    if (labels.length === 3) return t("pricing.feat.detect.all");
    if (!labels.length) return t("pricing.compare.value.notAvailable");
    return labels.join(", ");
  };
  const buildDetailedSections = (plan) => {
    const f = getPlanFeatures(plan);
    const graceDays = Number(f.storage_grace_period_days || 0);
    return [
      isVisible("section_detect") && {
        section: t("pricing.sec.detect"),
        items: [
          isVisible("row_detect_image") && { text: t("pricing.feat.detect.image"), ok: isMediaAllowed(plan, "image") },
          isVisible("row_detect_video") && { text: t("pricing.feat.detect.video"), ok: isMediaAllowed(plan, "video") },
          isVisible("row_detect_audio") && { text: t("pricing.feat.detect.audio"), ok: isMediaAllowed(plan, "audio") },
          { text: detectionSummaryLabel(plan), ok: detectionSummaryLabel(plan) !== t("pricing.compare.value.notAvailable") },
        ].filter(Boolean),
      },
      isVisible("section_report") && {
        section: t("pricing.sec.report"),
        items: [
          isVisible("row_report_score") && { text: t("pricing.feat.report.score"), ok: !!f.report_score },
          isVisible("row_report_ai_explain") && { text: t("pricing.compare.row.aiExplain"), ok: aiExplanationLabel(plan) !== t("pricing.compare.value.notAvailable") },
          isVisible("row_report_languages") && { text: t("pricing.feat.report.languages", { value: reportLanguagesLabel(plan) }), ok: true },
          isVisible("row_report_technical") && { text: t("pricing.feat.report.technical"), ok: !!f.report_technical },
          isVisible("row_report_pdf") && { text: t("pricing.feat.report.pdf"), ok: !!f.report_pdf_download },
        ],
      },
      isVisible("section_storage") && {
        section: t("pricing.sec.storage"),
        items: [
          isVisible("row_storage_history") && { text: t("pricing.feat.storage.history"), ok: !!f.storage_history },
          isVisible("row_storage_upload") && { text: t("pricing.feat.storage.upload"), ok: !!f.storage_file_upload },
          isVisible("row_storage_grace_days") && {
            text: t("pricing.feat.storage.graceDays", { days: graceDays }),
            ok: graceDays > 0,
          },
          isVisible("row_storage_credits") && { text: t("pricing.feat.storage.credits"), ok: !!f.credits_topup_enabled },
        ].filter(Boolean),
      },
      isVisible("section_support") && {
        section: t("pricing.sec.support"),
        items: [
          isVisible("row_support_center") && { text: t("pricing.feat.support.center"), ok: !!f.support_help_center },
          isVisible("row_support_email") && { text: emailSupportLabel(plan), ok: Number(f.support_email_sla_hours || 0) > 0 },
          isVisible("row_support_api") && { text: t("pricing.feat.support.api"), ok: !!f.support_api_access },
        ].filter(Boolean),
      },
    ]
      .filter(Boolean)
      .map((section) => ({ ...section, items: section.items.filter(Boolean) }))
      .filter((section) => section.items.length > 0);
  };
  const buildSections = (plan, includeDetailed = false) => [
    ...(isVisible("section_quota") ? [{ section: t("pricing.sec.quota"), items: buildQuotaItems(plan) }] : []),
    ...(includeDetailed ? buildDetailedSections(plan) : []),
  ];
  const getDisplayedPrice = (plan) => {
    if (billingCycle !== "annual" || !annualBillingEnabled || !plan?.annual_enabled) {
      return plan.price_idr;
    }
    return plan.annual_monthly_price_idr || plan.price_idr;
  };

  const fetchPlans = useCallback(async () => {
    setPlansLoading(true);
    setPlansError(null);
    try {
      const r = await axios.get(`${API}/subscription/plans`);
      const fetchedPlans = Array.isArray(r.data) ? r.data : [];
      setPlans(fetchedPlans);
      setPricingVisibility(normalizePricingVisibility(fetchedPlans?.[0]?.pricing_visibility));
      const annualConfig = extractAnnualPricingConfig(fetchedPlans);
      setAnnualBillingEnabled(annualConfig.annualBillingEnabled);
    } catch (err) {
      setPlans([]);
      setPlansError(
        err?.response?.data?.detail || t("pricing.error.loadFailed", { defaultValue: "Gagal memuat pricing. Coba lagi." }),
      );
    } finally {
      setPlansLoading(false);
    }
  }, [t]);

  useEffect(() => {
    fetchPlans();
  }, [fetchPlans]);

  useEffect(() => {
    axios
      .get(`${API}/credits/packages`)
      .then((r) => {
        const packages = Array.isArray(r.data) ? r.data : [];
        const mapped = packages
          .filter((p) => p?.is_active !== false && p?.is_public !== false)
          .map((p) => ({
            id: p.id,
            credits: p.credits,
            price: p.price_idr,
            perCredit: Math.round((p.price_idr || 0) / Math.max(p.credits || 1, 1)),
            bestseller: p?.is_most_used === true,
          }));
        if (mapped.length > 0) {
          const hasMostUsed = mapped.some((p) => p.bestseller);
          const bestPerCredit = Math.min(...mapped.map((p) => p.perCredit));
          setCreditPacks(
            mapped.map((p) => ({
              ...p,
              bestseller: hasMostUsed ? p.bestseller : p.perCredit === bestPerCredit,
            })),
          );
        } else {
          setCreditPacks([]);
        }
      })
      .catch(() => {
        setCreditPacks([]);
      });
  }, []);

  useEffect(() => {
    if (showComparison && shouldScrollComparison && comparisonRef.current) {
      comparisonRef.current.scrollIntoView({ behavior: "smooth", block: "start" });
      setShouldScrollComparison(false);
    }
  }, [showComparison, shouldScrollComparison]);

  useEffect(() => {
    if (!annualBillingEnabled && billingCycle !== "monthly") {
      setBillingCycle("monthly");
    }
  }, [annualBillingEnabled, billingCycle]);

  const handleUpgrade = () => {
    const appPricingUrl = `${APP_BASE_URL}/pricing`;
    window.location.assign(appPricingUrl);
  };

  const handleCreditPurchase = () => {
    const appPricingUrl = `${APP_BASE_URL}/pricing`;
    window.location.assign(appPricingUrl);
  };

  const TRUST_ITEMS = [
    { icon: Shield, key: "encrypted" },
    { icon: Server, key: "server" },
    { icon: Globe, key: "pdp" },
    { icon: CreditCard, key: "payment" },
  ];

  const FAQ_ITEMS = [1, 2, 3, 4, 5].map((n) => ({
    q: t(`pricing.faq.q${n}`),
    a: t(`pricing.faq.a${n}`),
  }));

  const orderedPlans = [...plans].sort(
    (a, b) => Number(a?.sort_order ?? 9999) - Number(b?.sort_order ?? 9999),
  );
  const freePlan = orderedPlans.find((p) => p.id === "free");
  const paidPlans = orderedPlans.filter(
    (p) =>
      p.id !== "free" &&
      p.is_active !== false &&
      p.is_public !== false,
  );
  const annualDiscountRefPlan = paidPlans[0] || null;
  const annualDiscountMode = annualDiscountRefPlan?.annual_discount_mode || "percent";
  const annualDiscountValue = Number(annualDiscountRefPlan?.annual_discount_value || 0);
  const showAnnualDiscountInfo = annualBillingEnabled && annualDiscountValue > 0;
  const comparisonPlans = freePlan
    ? [freePlan, ...paidPlans]
    : paidPlans;
  const openDetailedComparison = () => {
    if (showComparison) {
      setShowComparison(false);
      return;
    }
    setShowComparison(true);
    setShouldScrollComparison(true);
  };
  const comparisonSections = comparisonPlans.length
      ? (() => {
        const sectionDefs = [
          {
            key: "section_quota",
            section: t("pricing.sec.quota"),
            rows: [
              { key: "row_monthly_limit", label: t("pricing.compare.row.monthlyLimit"), value: (plan) => formatNumber(plan.limit) },
              { key: "row_daily_limit", label: t("pricing.compare.row.dailyLimit"), value: (plan) => formatNumber(plan.daily_limit) },
              { key: "row_file_limit", label: t("pricing.compare.row.fileLimit"), value: (plan) => formatFileLimit(plan.file_limit_mb) },
              {
                key: "row_cooldown",
                label: t("pricing.compare.row.cooldown"),
                value: (plan) => t("pricing.compare.value.seconds", { count: plan.cooldown_s ?? 0 }),
              },
              {
                key: "row_max_video_duration",
                label: t("pricing.compare.row.maxVideoDuration"),
                value: (plan) => formatDurationLimit(plan.max_video_duration_s),
              },
              {
                key: "row_max_audio_duration",
                label: t("pricing.compare.row.maxAudioDuration"),
                value: (plan) => formatDurationLimit(plan.max_audio_duration_s),
              },
            ],
          },
          {
            key: "section_detect",
            section: t("pricing.sec.detect"),
            rows: [
              { key: "row_detect_image", label: t("pricing.feat.detect.image"), value: (plan) => isMediaAllowed(plan, "image") },
              { key: "row_detect_video", label: t("pricing.feat.detect.video"), value: (plan) => isMediaAllowed(plan, "video") },
              { key: "row_detect_audio", label: t("pricing.feat.detect.audio"), value: (plan) => isMediaAllowed(plan, "audio") },
            ],
          },
          {
            key: "section_report",
            section: t("pricing.sec.report"),
            rows: [
              { key: "row_report_score", label: t("pricing.feat.report.score"), value: (plan) => !!getPlanFeatures(plan).report_score },
              {
                key: "row_report_ai_explain",
                label: t("pricing.compare.row.aiExplain"),
                tooltipKey: "aiExplain",
                value: (plan) => aiExplanationLabel(plan),
              },
              {
                key: "row_report_languages",
                label: t("pricing.compare.row.reportLanguages"),
                value: (plan) => reportLanguagesLabel(plan),
              },
              {
                key: "row_report_technical",
                label: t("pricing.feat.report.technical"),
                tooltipKey: "reportTechnical",
                value: (plan) => !!getPlanFeatures(plan).report_technical,
              },
              { key: "row_report_pdf", label: t("pricing.feat.report.pdf"), value: (plan) => !!getPlanFeatures(plan).report_pdf_download },
            ],
          },
          {
            key: "section_storage",
            section: t("pricing.sec.storage"),
            rows: [
              { key: "row_storage_history", label: t("pricing.feat.storage.history"), value: (plan) => !!getPlanFeatures(plan).storage_history },
              { key: "row_storage_upload", label: t("pricing.feat.storage.upload"), value: (plan) => !!getPlanFeatures(plan).storage_file_upload },
              {
                key: "row_storage_grace_days",
                label: t("pricing.compare.row.gracePeriod"),
                value: (plan) => {
                  const days = Number(getPlanFeatures(plan).storage_grace_period_days || 0);
                  return days > 0 ? t("pricing.compare.value.days", { count: days }) : false;
                },
              },
              { key: "row_storage_credits", label: t("pricing.feat.storage.credits"), value: (plan) => !!getPlanFeatures(plan).credits_topup_enabled },
            ],
          },
          {
            key: "section_support",
            section: t("pricing.sec.support"),
            rows: [
              { key: "row_support_center", label: t("pricing.feat.support.center"), value: (plan) => !!getPlanFeatures(plan).support_help_center },
              { key: "row_support_email", label: t("pricing.compare.row.emailSupport"), value: (plan) => emailSupportLabel(plan) },
              { key: "row_support_api", label: t("pricing.feat.support.api"), value: (plan) => !!getPlanFeatures(plan).support_api_access },
            ],
          },
        ];

        return sectionDefs
          .filter((sec) => isVisible(sec.key))
          .map((sec) => ({
          section: sec.section,
          rows: sec.rows
            .filter((row) => isVisible(row.key))
            .map((row) => {
            const rowData = { label: row.label, tooltipKey: row.tooltipKey || "" };
            comparisonPlans.forEach((plan) => {
              rowData[plan.id] = row.value(plan);
            });
            return rowData;
          }),
        }))
          .filter((sec) => sec.rows.length > 0);
      })()
    : [];
  const annualDiscountBadge = annualDiscountMode === "percent"
    ? t("pricing.billing.discountInfoPercent", { value: annualDiscountValue })
    : t("pricing.billing.discountInfoNominal", { value: `Rp ${formatNumber(annualDiscountValue)}` });

  return (
    <main className="min-h-screen" data-testid="pricing-page">
      <div className="max-w-7xl mx-auto px-4 md:px-8 py-14">
        {/* ── Hero ── */}
        <motion.div
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-center mb-10"
        >
          <span className="text-xs font-mono uppercase tracking-widest text-muted-foreground">
            {t("pricing.badge")}
          </span>
          <h1 className="font-heading text-4xl md:text-5xl font-bold tracking-tight mt-2">
            {t("pricing.title")}
          </h1>
          <p className="text-muted-foreground mt-3 text-sm md:text-base max-w-xl mx-auto">
            {t("pricing.subtitle")}
          </p>
        </motion.div>

        {/* ── Trust bar ── */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.1 }}
          className="flex flex-wrap justify-center gap-x-6 gap-y-2 mb-10"
        >
          {TRUST_ITEMS.map(({ icon: Icon, key }) => (
            <span
              key={key}
              className="flex items-center gap-1.5 text-sm text-muted-foreground"
            >
              <Icon className="w-3.5 h-3.5 text-verdict-real flex-shrink-0" />
              {t(`pricing.trust.${key}`)}
            </span>
          ))}
        </motion.div>

        {/* ── Plans ── */}
        {plansLoading && (
          <div className="mb-14 rounded-md border border-border bg-card p-6 text-center text-sm text-muted-foreground">
            {t("common.loading")}
          </div>
        )}
        {!plansLoading && plansError && (
          <div className="mb-14 rounded-md border border-verdict-fake/30 bg-verdict-fake/5 p-6 text-center">
            <p className="text-sm text-verdict-fake">{plansError}</p>
            <Button
              onClick={fetchPlans}
              variant="outline"
              className="mt-3 rounded-sm"
              data-testid="pricing-retry-btn"
            >
              {t("common.retry", { defaultValue: "Coba Lagi" })}
            </Button>
          </div>
        )}
        {!plansLoading && !plansError && plans.length === 0 && (
          <div className="mb-14 rounded-md border border-border bg-card p-6 text-center text-sm text-muted-foreground">
            {t("pricing.noPaidPlans")}
          </div>
        )}
        {!plansLoading && !plansError && plans.length > 0 && (
          <div className="mb-14 space-y-5">
            {freePlan && (
              <motion.div
                variants={container}
                initial="hidden"
                animate="show"
                className="rounded-md border-2 border-border bg-card p-4 md:p-5"
                data-testid="plan-free-row"
              >
                <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
                  <div>
                    <div className="flex items-center gap-2">
                      <span className="font-heading text-lg font-semibold text-muted-foreground">
                        {getPlanLabel(freePlan)}
                      </span>
                    </div>
                    <div className="mt-2 flex flex-col gap-2 sm:flex-row sm:items-center sm:gap-3">
                      <div className="font-heading text-3xl font-bold tracking-tight leading-none whitespace-nowrap">
                        {t("pricing.free")}
                      </div>
                      <div className="flex flex-wrap gap-2">
                        {(isVisible("section_quota") ? buildQuotaItems(freePlan, false) : []).map((item) => (
                          <span
                            key={item.text}
                            className="text-sm font-mono px-2.5 py-1 rounded-full border border-border bg-secondary/40 text-muted-foreground"
                          >
                            {item.text}
                          </span>
                        ))}
                      </div>
                    </div>
                  </div>
                  <div className="w-full md:w-52">
                    {maintenanceMode ? (
                      <div className="w-full rounded-sm border border-amber-400/30 bg-amber-500/10 px-3 py-2 text-center text-xs font-mono uppercase tracking-wider text-amber-300">
                        Segera Hadir
                      </div>
                    ) : (
                      <a href={`${APP_BASE_URL}/register`} data-testid="plan-free-cta">
                        <Button className="w-full text-sm bg-brand-primary hover:bg-brand-primary/90 text-white">
                          {t("pricing.cta.freeNow")}
                        </Button>
                      </a>
                    )}
                  </div>
                </div>
              </motion.div>
            )}

            {annualBillingEnabled && (
              <div className="flex justify-center mb-1">
                <div className="inline-flex flex-wrap items-center justify-center gap-2">
                  <div className="inline-flex items-center gap-1 rounded-full border border-border bg-card p-1">
                    <button
                      type="button"
                      onClick={() => setBillingCycle("monthly")}
                      className={`rounded-full px-3 py-1 text-sm transition-colors ${
                        billingCycle === "monthly"
                          ? "bg-brand-primary text-white"
                          : "text-muted-foreground hover:text-foreground"
                      }`}
                      data-testid="billing-monthly-btn"
                    >
                      {t("pricing.billing.monthly")}
                    </button>
                    <button
                      type="button"
                      onClick={() => setBillingCycle("annual")}
                      className={`rounded-full px-3 py-1 text-sm transition-colors ${
                        billingCycle === "annual"
                          ? "bg-brand-primary text-white"
                          : "text-muted-foreground hover:text-foreground"
                      }`}
                      data-testid="billing-annual-btn"
                    >
                      {t("pricing.billing.annual")}
                    </button>
                  </div>
                  <span
                    className={`rounded-md border px-2.5 py-1 text-xs font-semibold transition-opacity ${
                      showAnnualDiscountInfo
                        ? "border-amber-300/70 bg-amber-300/90 text-slate-900"
                        : "border-border text-muted-foreground"
                    } ${billingCycle === "annual" ? "opacity-100" : "opacity-45"}`}
                  >
                    {showAnnualDiscountInfo ? annualDiscountBadge : t("pricing.billing.discountOff")}
                  </span>
                </div>
              </div>
            )}

            <motion.div
              variants={container}
              initial="hidden"
              animate="show"
              className="grid gap-4 justify-center [grid-template-columns:repeat(auto-fit,minmax(290px,360px))]"
            >
              {paidPlans.map((plan) => {
                const Icon = ICONS[plan.id] || Sparkles;
                const isPopular = plan.popular;
                const planLabel = getPlanLabel(plan);
                const sections = buildSections(plan, false);
                const annualSelected = billingCycle === "annual" && annualBillingEnabled && plan.annual_enabled;
                const displayedPrice = getDisplayedPrice(plan);

                return (
                  <motion.div
                    key={plan.id}
                    variants={fadeUp}
                    data-testid={`plan-${plan.id}`}
                    className={`relative w-full rounded-md border-2 ${getPlanColorClass(plan.id)} bg-card flex flex-col p-5 ${
                      isPopular ? "pt-8 shadow-[0_0_30px_rgba(34,211,238,0.2)]" : ""
                    }`}
                  >
                    {isPopular && (
                      <div className="absolute -top-3 left-1/2 -translate-x-1/2 whitespace-nowrap">
                        <span className="text-xs font-mono bg-brand-primary text-white px-3 py-0.5 rounded-full">
                          {t("pricing.popular")}
                        </span>
                      </div>
                    )}

                    <div className="flex items-center gap-1.5 mb-3">
                      {Icon && <Icon className={`w-4 h-4 ${getBadgeColorClass(plan.id)}`} />}
                      <span className={`font-heading text-base font-semibold ${getBadgeColorClass(plan.id)}`}>{planLabel}</span>
                    </div>

                    <div className="mb-4">
                      <div className="flex items-end gap-2">
                        <div className="font-heading text-3xl font-bold tracking-tight leading-none">
                          <span className="text-sm font-normal text-muted-foreground">Rp </span>
                          {formatNumber(displayedPrice)}
                        </div>
                        <span className="text-sm text-muted-foreground leading-none">{t("pricing.perMonth")}</span>
                      </div>
                      {annualSelected && (
                        <div className="mt-1.5 flex flex-wrap items-center gap-1.5">
                          <span className="text-xs font-mono px-2 py-0.5 rounded-full bg-brand-primary/10 text-brand-primary border border-brand-primary/20">
                            {t("pricing.billing.yearlyBilled")}
                          </span>
                          {plan.annual_discount_amount_idr > 0 && (
                            <span className="text-xs text-muted-foreground">
                              {t("pricing.billing.annualSave", {
                                value: `Rp ${formatNumber(plan.annual_discount_amount_idr)}`,
                              })}
                            </span>
                          )}
                        </div>
                      )}
                    </div>

                    <div className="flex-1 mb-5 space-y-0">
                      {sections.map((sec) => (
                        <div key={sec.section}>
                          <div className="text-sm font-mono uppercase tracking-widest text-muted-foreground/50 pt-2.5 pb-1 mt-1 border-t border-border/40 first:border-t-0 first:pt-0 first:mt-0">
                            {sec.section}
                          </div>
                          {sec.items.map((item) => (
                            <div key={item.text} className="flex items-start gap-1.5 py-0.5">
                              {item.ok === true && (
                                <Check className="w-3.5 h-3.5 text-verdict-real flex-shrink-0 mt-0.5" />
                              )}
                              {item.ok === false && (
                                <span className="w-3.5 flex-shrink-0 mt-0.5 text-xs text-muted-foreground/30 leading-none text-center">
                                  —
                                </span>
                              )}
                              {item.ok === "soon" && (
                                <span className="w-3.5 flex-shrink-0 mt-0.5 text-xs text-amber-500 leading-none">
                                  ◐
                                </span>
                              )}
                              <span className={`text-sm leading-relaxed ${item.ok === false ? "text-muted-foreground/40" : "text-muted-foreground"}`}>
                                {item.text}
                                {item.ok === "soon" && (
                                  <span className="ml-1 text-xs font-mono font-bold bg-amber-500/10 text-amber-500 px-1 py-px rounded">
                                    {t("pricing.feat.soon")}
                                  </span>
                                )}
                              </span>
                            </div>
                          ))}
                        </div>
                      ))}
                    </div>

                    {maintenanceMode ? (
                      <div className="w-full rounded-sm border border-amber-400/30 bg-amber-500/10 px-3 py-2 text-center text-xs font-mono uppercase tracking-wider text-amber-300">
                        Segera Hadir
                      </div>
                    ) : (
                      <Button
                        onClick={() => handleUpgrade(plan.id)}
                        className={`w-full text-sm gap-1.5 ${getButtonClass(plan.id, false)}`}
                        data-testid={`upgrade-${plan.id}-btn`}
                      >
                        {t("pricing.cta.upgrade")} {planLabel} <ArrowRight className="w-3 h-3" />
                      </Button>
                    )}
                  </motion.div>
                );
              })}
              {paidPlans.length === 0 && (
                <div className="w-full rounded-md border border-dashed border-border p-6 text-center text-sm text-muted-foreground">
                  {t("pricing.noPaidPlans")}
                </div>
              )}
            </motion.div>

            <AnimatePresence initial={false}>
              {showComparison && (
                <motion.section
                  ref={comparisonRef}
                  id="pricing-comparison"
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: 10 }}
                  className="rounded-md border border-border bg-card/40 p-4 md:p-5"
                  data-testid="pricing-comparison-section"
                >
                  <h3 className="font-heading text-xl font-semibold mb-1">
                    {t("pricing.compare.detailTitle")}
                  </h3>
                  <p className="text-sm md:text-base text-muted-foreground mb-4">
                    {t("pricing.compare.detailSubtitle")}
                  </p>

                  <div className="overflow-x-auto rounded-sm border border-border">
                    <table className="w-full min-w-[860px] text-sm">
                      <thead className="bg-secondary/40">
                        <tr className="border-b border-border">
                          <th className="sticky left-0 z-20 bg-secondary/40 text-left px-3 py-2.5 font-mono uppercase tracking-wider text-muted-foreground min-w-[240px]">
                            {t("pricing.compare.col.feature")}
                          </th>
                          {comparisonPlans.map((plan) => (
                            <th
                              key={plan.id}
                              className="px-3 py-2.5 text-center align-middle font-mono uppercase tracking-wider text-muted-foreground min-w-[150px]"
                            >
                              {getPlanLabel(plan)}
                            </th>
                          ))}
                        </tr>
                      </thead>
                      {comparisonSections.map((sec) => (
                        <tbody key={sec.section}>
                          <tr className="border-b border-border/60 bg-background/40">
                            <td
                              colSpan={comparisonPlans.length + 1}
                              className="px-3 py-2 text-sm font-semibold tracking-wide text-muted-foreground"
                            >
                              {sec.section}
                            </td>
                          </tr>
                          {sec.rows.map((row) => (
                            <tr key={`${sec.section}-${row.label}`} className="border-b border-border/40">
                              <td className="sticky left-0 z-10 bg-card px-3 py-2.5 text-muted-foreground align-middle">
                                <span className="inline-flex items-center gap-1">
                                  {row.label}
                                  {row.tooltipKey && (
                                    <TooltipProvider delayDuration={100}>
                                      <Tooltip>
                                        <TooltipTrigger asChild>
                                          <button
                                            type="button"
                                            className="inline-flex items-center text-muted-foreground/70 hover:text-foreground focus:outline-none focus:ring-1 focus:ring-brand-primary rounded-sm"
                                            aria-label={t("pricing.compare.tooltip.label")}
                                          >
                                            <Info className="w-3.5 h-3.5" />
                                          </button>
                                        </TooltipTrigger>
                                        <TooltipContent
                                          side="top"
                                          align="start"
                                          sideOffset={8}
                                          className="z-[80] max-w-[300px] whitespace-normal break-words text-wrap leading-snug text-[12px] px-2.5 py-2"
                                        >
                                          {row.tooltipKey === "aiExplain" ? (
                                            <div className="space-y-1">
                                              <p>
                                                <span className="font-semibold">{t("pricing.compare.tooltip.ai.noneLabel")}</span>{" "}
                                                {t("pricing.compare.tooltip.ai.noneDesc")}
                                              </p>
                                              <p>
                                                <span className="font-semibold">{t("pricing.compare.tooltip.ai.basicLabel")}</span>{" "}
                                                {t("pricing.compare.tooltip.ai.basicDesc")}
                                              </p>
                                              <p>
                                                <span className="font-semibold">{t("pricing.compare.tooltip.ai.fullLabel")}</span>{" "}
                                                {t("pricing.compare.tooltip.ai.fullDesc")}
                                              </p>
                                            </div>
                                          ) : (
                                            t("pricing.compare.tooltip.reportTechnical")
                                          )}
                                        </TooltipContent>
                                      </Tooltip>
                                    </TooltipProvider>
                                  )}
                                </span>
                              </td>
                              {comparisonPlans.map((plan) => {
                                const value = row[plan.id];
                                return (
                                  <td key={`${row.label}-${plan.id}`} className="px-3 py-2.5 text-muted-foreground text-center align-middle">
                                    {value === true && (
                                      <span className="inline-flex items-center justify-center">
                                        <Check className="w-4 h-4 text-verdict-real" />
                                      </span>
                                    )}
                                    {value === "soon" && (
                                      <span className="inline-flex items-center justify-center gap-1">
                                        ◐
                                        {t("pricing.feat.soon")}
                                      </span>
                                    )}
                                    {(value === false || value === null || value === undefined || value === "") && "—"}
                                    {value !== true && value !== "soon" && value !== false && value !== null && value !== undefined && value !== "" && (
                                      <span className="whitespace-normal break-words">{value}</span>
                                    )}
                                  </td>
                                );
                              })}
                            </tr>
                          ))}
                        </tbody>
                      ))}
                    </table>
                  </div>
                </motion.section>
              )}
            </AnimatePresence>
            <div className={`text-center ${showComparison ? "pt-3" : "pt-1"}`}>
              <button
                type="button"
                onClick={openDetailedComparison}
                className="text-sm font-medium text-brand-primary hover:underline"
                data-testid="pricing-link-comparison"
              >
                {showComparison ? t("pricing.compare.hide") : t("pricing.compare.show")}
              </button>
            </div>
          </div>
        )}

        {/* ── Credit Packages ── */}
        {creditPacks.length > 0 && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.25 }}
          className="mb-14"
        >
          <div className="text-center mb-6">
            <h2 className="font-heading text-2xl font-bold tracking-tight">
              {t("pricing.credit.title")}
            </h2>
            <p className="text-sm text-muted-foreground mt-1">
              {t("pricing.credit.subtitle")}
            </p>
          </div>

          {/* Credit cost per type */}
          <div className="flex flex-wrap justify-center gap-6 mb-6 border border-border rounded-md py-4">
            {[
              { emoji: "🖼️", labelKey: "image", costKey: "1credit" },
              { emoji: "🎬", labelKey: "video", costKey: "3credits" },
              { emoji: "🔊", labelKey: "audio", costKey: "2credits" },
            ].map(({ emoji, labelKey, costKey }) => (
              <div
                key={labelKey}
                className="flex flex-col items-center gap-1 px-6"
              >
                <span className="text-2xl">{emoji}</span>
                <span className="text-sm font-semibold">
                  {t(`pricing.credit.${labelKey}`)}
                </span>
                <span className="text-sm font-mono text-brand-primary">
                  {t(`pricing.credit.${costKey}`)}
                </span>
              </div>
            ))}
          </div>

          {/* Credit packs grid */}
          <div className="grid gap-3 justify-center [grid-template-columns:repeat(auto-fit,minmax(220px,260px))]">
            {creditPacks.map(({ id, credits, price, perCredit, bestseller }) => (
              <div
                key={id}
                className={`relative w-full rounded-md border bg-card p-4 text-center
                  ${bestseller ? "border-verdict-real shadow-[0_0_20px_rgba(16,185,129,0.1)]" : "border-border"}`}
              >
                {bestseller && (
                  <div className="absolute -top-2.5 left-1/2 -translate-x-1/2 whitespace-nowrap">
                    <span className="text-xs font-mono bg-verdict-real text-white px-2 py-0.5 rounded-full">
                      {t("pricing.credit.bestseller")}
                    </span>
                  </div>
                )}
                <div className="font-heading text-3xl font-bold">{credits}</div>
                <div className="text-sm text-muted-foreground mb-3">
                  {t("pricing.credit.unit")}
                </div>
                <div className="text-base font-bold text-brand-primary">
                  Rp {formatNumber(price)}
                </div>
                <div className="text-sm text-muted-foreground mb-3">
                  Rp {formatNumber(perCredit)}{" "}
                  {t("pricing.credit.perCredit")}
                </div>
                {maintenanceMode ? (
                  <div className="w-full rounded-sm border border-amber-400/30 bg-amber-500/10 px-3 py-2 text-center text-xs font-mono uppercase tracking-wider text-amber-300">
                    Segera Hadir
                  </div>
                ) : (
                  <Button
                    onClick={() => handleCreditPurchase(id)}
                    variant="outline"
                    className={`w-full text-sm h-8
                    ${bestseller ? "border-verdict-real text-verdict-real hover:bg-verdict-real/10" : ""}`}
                  >
                    {t("pricing.credit.buy")}
                  </Button>
                )}
              </div>
            ))}
          </div>
        </motion.div>
        )}

        {/* ── Retention Policy ── */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.3 }}
          className="border border-border rounded-md p-5 mb-14"
        >
          <h3 className="text-sm font-semibold mb-4">
            📋 {t("pricing.retention.title")}
          </h3>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-border">
                  <th className="text-left py-2 pr-4 font-mono uppercase tracking-wider text-muted-foreground text-sm">
                    {t("pricing.retention.col.data")}
                  </th>
                  <th className="text-left py-2 font-mono uppercase tracking-wider text-muted-foreground text-sm">
                    {t("pricing.retention.col.policy")}
                  </th>
                </tr>
              </thead>
              <tbody>
                {[1, 2, 3, 4, 5].map((n) => (
                  <tr
                    key={n}
                    className="border-b border-border/50 last:border-0"
                  >
                    <td className="py-2.5 pr-4 font-medium text-foreground">
                      {t(`pricing.retention.r${n}.data`)}
                    </td>
                    <td className="py-2.5 text-muted-foreground">
                      {t(`pricing.retention.r${n}.policy`)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </motion.div>

        {/* ── FAQ ── */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.35 }}
          className="mb-14"
        >
          <h2 className="font-heading text-2xl font-bold text-center mb-6">
            {t("pricing.faq.title")}
          </h2>
          <div className="space-y-2">
            {FAQ_ITEMS.map((faq, i) => (
              <div
                key={i}
                className="border border-border rounded-md overflow-hidden"
              >
                <button
                  onClick={() => setOpenFaq(openFaq === i ? null : i)}
                  className="w-full flex items-center justify-between px-5 py-4 text-sm font-medium text-left hover:bg-secondary/50 transition-colors"
                >
                  {faq.q}
                  <ChevronDown
                    className={`w-4 h-4 text-muted-foreground flex-shrink-0 ml-3 transition-transform ${openFaq === i ? "rotate-180" : ""}`}
                  />
                </button>
                <AnimatePresence initial={false}>
                  {openFaq === i && (
                    <motion.div
                      key="answer"
                      initial={{ height: 0, opacity: 0 }}
                      animate={{ height: "auto", opacity: 1 }}
                      exit={{ height: 0, opacity: 0 }}
                      transition={{ duration: 0.2 }}
                    >
                      <p className="px-5 pb-4 text-sm text-muted-foreground leading-relaxed">
                        {faq.a}
                      </p>
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
            ))}
          </div>
        </motion.div>

        {/* ── Bottom CTA ── */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.4 }}
          className="text-center border border-border rounded-md py-10 px-6"
        >
          <h3 className="font-heading text-2xl font-bold mb-2">
            {t("pricing.cta.title")}
          </h3>
          <p className="text-muted-foreground text-sm mb-6">
            {t("pricing.cta.desc")}
          </p>
          {maintenanceMode ? (
            <div className="inline-flex rounded-sm border border-amber-400/30 bg-amber-500/10 px-4 py-2 text-xs font-mono uppercase tracking-wider text-amber-300">
              Segera Hadir
            </div>
          ) : (
            <a href={`${APP_BASE_URL}/register`}>
              <Button className="bg-brand-primary hover:bg-brand-primary/90 text-white gap-2 px-8">
                {t("pricing.cta.btn")} <ArrowRight className="w-4 h-4" />
              </Button>
            </a>
          )}
        </motion.div>

        {/* ── Payment footnote ── */}
        <p className="text-sm font-mono text-muted-foreground text-center mt-6">
          {t("pricing.footer")}
        </p>
      </div>
    </main>
  );
}
